import { useBottomSheet } from '@gorhom/bottom-sheet';
import { theme } from 'aimo-ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityActionEvent } from 'react-native';
import Animated, {
  Easing,
  SharedValue,
  useAnimatedStyle,
  WithTimingConfig,
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import { testIdentifiers } from 'utils/testIdentifiers';

const BGView = styled(Animated.View)<{
  bgColor?: string;
}>`
  height: 100%;
  width: 100%;
  padding: 20px;
  background-color: ${(p) => p.bgColor || theme.colors.white};
  position: absolute;
`;

const HandleView = styled.View`
  padding: 10px;
`;

const DragHandle = styled.View<{ handleColor?: string }>`
  align-self: center;
  width: 30px;
  height: 4px;
  border-radius: 4px;
  background-color: ${(p) => p.handleColor || theme.colors.gray100};
`;

// The theme radius tokens are CSS shorthand strings ("40px 40px 0px 0px" =
// top-left top-right bottom-right bottom-left) — a scalar parseInt would
// collapse them to all four corners and round the sheet's bottom edge, which
// shows above the transparent navigation bar on edge-to-edge Android.
const cornerRadii = (shorthand: string) => {
  const [
    topLeft,
    topRight = topLeft,
    bottomRight = topLeft,
    bottomLeft = topRight,
  ] = shorthand
    .trim()
    .split(/\s+/)
    .map((value) => Number.parseInt(value, 10));
  return {
    borderTopLeftRadius: topLeft,
    borderTopRightRadius: topRight,
    borderBottomRightRadius: bottomRight,
    borderBottomLeftRadius: bottomLeft,
  };
};

export const BackgroundComponent = ({
  bgColor,
  snappingPoints,
  borderRadius,
  sheetIndex,
}: {
  bgColor?: string;
  snappingPoints?: (string | number)[];
  borderRadius?: string;
  sheetIndex?: SharedValue<number>;
}) => {
  const fullScreen =
    sheetIndex?.value === 1 &&
    snappingPoints &&
    snappingPoints.length > 0 &&
    snappingPoints[snappingPoints.length - 1] === '100%';

  const radii = fullScreen
    ? cornerRadii('0px')
    : cornerRadii(borderRadius ?? theme.borderRadius.card);

  const style = useAnimatedStyle(() => {
    return radii;
  });

  return <BGView bgColor={bgColor} style={style} />;
};

type HandleProps = {
  handleColor?: string;
};

export const HandleComponent: React.FC<HandleProps> = ({ handleColor }) => {
  return (
    <HandleView {...testIdentifiers('bottom-sheet-handle')}>
      <DragHandle handleColor={handleColor} />
    </HandleView>
  );
};

type AccessibleHandleProps = HandleProps & {
  snapPointCount?: number;
  canClose?: boolean;
};

export const AccessibleHandle = ({
  handleColor,
  snapPointCount = 1,
  canClose = false,
}: AccessibleHandleProps) => {
  const { t } = useTranslation();
  const { animatedIndex, snapToIndex, close } = useBottomSheet();
  const pointCount = Math.max(snapPointCount, 1);
  const adjustable = pointCount > 1 || canClose;
  const [currentIndex, setCurrentIndex] = useState(0);

  const onAccessibilityAction = (event: AccessibilityActionEvent) => {
    const { actionName } = event.nativeEvent;
    if (actionName !== 'increment' && actionName !== 'decrement') {
      return;
    }
    const max = pointCount - 1;
    const min = canClose ? -1 : 0;
    const raw = animatedIndex.value;
    const current = Math.min(
      Math.max(Number.isFinite(raw) ? Math.round(raw) : 0, min),
      max
    );
    const next = current + (actionName === 'increment' ? 1 : -1);
    if (next < min || next > max) {
      return;
    }
    // Update before snap so accessibilityValue is current when iOS reads it.
    setCurrentIndex(next);
    if (next < 0) {
      close();
    } else {
      snapToIndex(next);
    }
  };

  if (!adjustable) {
    return <HandleComponent handleColor={handleColor} />;
  }

  const valueMax = canClose ? pointCount : pointCount - 1;

  return (
    <HandleView
      accessible
      accessibilityRole="adjustable"
      accessibilityLabel={t('accessibility.bottomSheet.position', {
        defaultValue: 'Bottom sheet position',
      })}
      accessibilityHint={t('accessibility.bottomSheet.positionHint', {
        defaultValue: 'Swipe up or down to adjust the position.',
      })}
      accessibilityValue={{
        min: 0,
        max: valueMax,
        now: Math.min(
          Math.max(canClose ? currentIndex + 1 : currentIndex, 0),
          valueMax
        ),
      }}
      accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
      onAccessibilityAction={onAccessibilityAction}
      {...testIdentifiers('bottom-sheet-handle')}>
      <DragHandle handleColor={handleColor} />
    </HandleView>
  );
};

export const NoHandleComponent = () => <HandleView />;

export const animationConfigSheetModal: WithTimingConfig = {
  duration: 600,
  easing: Easing.bezier(0.25, 0.1, 0.25, 1),
};

export const animationConfigSheet: WithTimingConfig = {
  duration: 300,
  easing: Easing.inOut(Easing.ease),
};
